export type Participant = { name: string; address: string };

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const monthDayFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const shortDateFmt = new Intl.DateTimeFormat(undefined, {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
});
const absoluteFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * List timestamps are scanned, not read: the closer a message is to now, the
 * more precision earns its pixels. Everything collapses to at most 8 characters
 * so the 64px right-aligned column never reflows.
 */
export function formatTimestamp(epochSeconds: number, now: number = Date.now()): string {
  const ms = epochSeconds * 1000;
  const then = new Date(ms);
  if (!Number.isFinite(ms)) return '';

  const elapsed = now - ms;
  if (elapsed < MINUTE) return 'now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`;

  const nowDate = new Date(now);
  const dayDelta = Math.round((startOfDay(nowDate) - startOfDay(then)) / 86_400_000);
  if (dayDelta === 0) return timeFmt.format(then);
  if (dayDelta === 1) return 'Yesterday';
  if (dayDelta > 1 && dayDelta < 7) return weekdayFmt.format(then);
  if (then.getFullYear() === nowDate.getFullYear()) return monthDayFmt.format(then);
  return shortDateFmt.format(then);
}

/** The unabbreviated form, for the `title` attribute of any relative timestamp. */
export function absoluteTimestamp(epochSeconds: number): string {
  const ms = epochSeconds * 1000;
  if (!Number.isFinite(ms)) return '';
  return absoluteFmt.format(new Date(ms));
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const decimals = unit === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toFixed(decimals)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

function firstGlyph(word: string): string {
  const match = word.match(/[\p{L}\p{N}]/u);
  return match ? match[0] : '';
}

function stripQuotes(name: string): string {
  return name.trim().replace(/^["'](.*)["']$/s, '$1').trim();
}

/**
 * Initials for an avatar. Email addresses are decomposed on their separators so
 * `first.last@…` reads as "FL" rather than "F".
 */
export function initialsFor(nameOrAddress: string): string {
  const raw = stripQuotes(nameOrAddress ?? '');
  if (!raw) return '?';

  let source = raw;
  if (!/\s/.test(raw) && raw.includes('@')) {
    source = raw.slice(0, raw.indexOf('@')).replace(/[._\-+]+/g, ' ');
  }

  const words = source.split(/\s+/).filter((w) => firstGlyph(w) !== '');
  const first = words[0] ?? '';
  const last = words[words.length - 1] ?? '';
  if (words.length === 0) return raw[0]?.toUpperCase() ?? '?';
  if (words.length === 1) return firstGlyph(first).toUpperCase();
  return (firstGlyph(first) + firstGlyph(last)).toUpperCase();
}

/** FNV-1a. Stable across sessions and machines, which is the whole point. */
function hash32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic 0–359 hue for an address, used as `--h` on avatar chips. */
export function hueForAddress(address: string): number {
  return hash32((address ?? '').trim().toLowerCase()) % 360;
}

/**
 * A saturated form of the address hue. Avatars mix this against surface tokens
 * so they stay legible in both themes; use `hueForAddress` for that path.
 */
export function colorForAddress(address: string): string {
  return `hsl(${hueForAddress(address)} 45% 48%)`;
}

function localPart(address: string): string {
  const at = address.indexOf('@');
  return at > 0 ? address.slice(0, at) : address;
}

function fullName(p: Participant): string {
  const name = stripQuotes(p.name ?? '');
  return name || p.address || '';
}

function shortName(p: Participant): string {
  const name = stripQuotes(p.name ?? '');
  if (name) return name.split(/\s+/)[0] ?? name;
  return localPart(p.address ?? '');
}

/**
 * "Alice, Bob" for a group, "Alice Kowalski" for one sender, and always "me"
 * where the reader appears. Deduped by address so a 12-message thread with two
 * humans in it does not print twelve names.
 */
export function formatParticipants(
  participants: Participant[],
  selfAddresses: string[] = [],
  max = 3,
): string {
  const self = new Set(selfAddresses.map((a) => a.trim().toLowerCase()).filter(Boolean));
  const seen = new Set<string>();
  const unique: Participant[] = [];

  for (const p of participants ?? []) {
    const key = (p.address ?? '').trim().toLowerCase() || fullName(p).toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(p);
  }

  if (unique.length === 0) return 'Unknown';

  const names = unique.map((p) => {
    if (self.has((p.address ?? '').trim().toLowerCase())) return 'me';
    return unique.length === 1 ? fullName(p) : shortName(p);
  });

  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max}`;
}

/** The domain of an address, lowercased. Empty string when there isn't one. */
export function domainOf(address: string): string {
  const at = (address ?? '').lastIndexOf('@');
  return at >= 0 ? address.slice(at + 1).trim().toLowerCase() : '';
}
